import React, { useState, useEffect } from 'react';
import { Menu, Monitor, User, DollarSign, FileText, Settings, LogOut, ChevronLeft, ChevronRight, Building2, ClipboardCheck, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate, useLocation, Outlet, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useEmployees } from '../hooks/useEmployees';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, session } = useAuth();
  const { employees, loading: employeesLoading } = useEmployees();
  const [currentEmployee, setCurrentEmployee] = useState<null | { firstName: string }>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return window.innerWidth < 768;
  });
  
  useEffect(() => {
    if (session?.user?.email && !employeesLoading) {
      const employee = employees.find(e => e.email === session.user.email);
      setCurrentEmployee(employee || null);
    } else {
      setCurrentEmployee(null);
    }
  }, [session?.user?.email, employees, employeesLoading]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect non-admin users trying to access admin routes
  if (!isAdmin && ['/employees', '/banking', '/payroll', '/reports', '/compliance', '/settings', '/timesheet'].includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  const handleLogout = async () => {
    try {
      localStorage.clear();
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.debug('Supabase signOut error (non-critical):', error);
      }
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.href = '/';
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      <aside 
        className={`fixed top-0 left-0 h-screen bg-white border-r border-gray-200 transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div className={`p-4 border-b border-gray-200 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Payrolli</span>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-1 rounded-lg hover:bg-gray-100 ${isCollapsed ? 'mx-auto' : ''}`}
            title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>
        
        {!isCollapsed && !isAdmin && currentEmployee && (
          <div className="px-4 py-2">
            <p className="text-sm text-gray-600">
              Welcome {currentEmployee.firstName}!
            </p>
          </div>
        )}
        
        <nav className="p-2">
          <ul className="space-y-2">
            <li>
              <Link
                to="/"
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  isActive('/')
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                title="Dashboard"
              >
                <Monitor className="h-5 w-5" />
                {!isCollapsed && <span>Dashboard</span>}
              </Link>
            </li>

            <li>
              <Link
                to="/timeclock"
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  isActive('/timeclock')
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                title="Time Clock"
              >
                <Clock className="h-5 w-5" />
                {!isCollapsed && <span>Time Clock</span>}
              </Link>
            </li>

            {isAdmin && (
              <>
                <li>
                  <Link
                    to="/employees"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/employees')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Employees"
                  >
                    <User className="h-5 w-5" />
                    {!isCollapsed && <span>Employees</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/banking"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/banking')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Banking"
                  >
                    <Building2 className="h-5 w-5" />
                    {!isCollapsed && <span>Banking</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/timesheet"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/timesheet')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Timesheets"
                  >
                    <Clock className="h-5 w-5" />
                    {!isCollapsed && <span>Timesheets</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/payroll"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/payroll')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Payroll"
                  >
                    <DollarSign className="h-5 w-5" />
                    {!isCollapsed && <span>Payroll</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/reports"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/reports')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Reports"
                  >
                    <FileText className="h-5 w-5" />
                    {!isCollapsed && <span>Reports</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/compliance"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/compliance')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Compliance"
                  >
                    <ClipboardCheck className="h-5 w-5" />
                    {!isCollapsed && <span>Compliance</span>}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/settings"
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isActive('/settings')
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Settings"
                  >
                    <Settings className="h-5 w-5" />
                    {!isCollapsed && <span>Settings</span>}
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        <div className={`absolute bottom-0 left-0 right-0 p-2 border-t border-gray-200 ${isCollapsed ? 'text-center' : ''}`}>
          <button 
            onClick={handleLogout}
            className={`flex items-center gap-2 p-2 text-gray-700 hover:bg-gray-50 rounded-lg ${
              isCollapsed ? 'justify-center w-full' : ''
            }`}
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <main className={`transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'} p-8`}>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;